import 'assets/css/modal.css';
import 'styles/custom.css';
import * as React from 'react';
import { getRegionDetails } from "../components/Requests/Requests";
import {
    StandardScreen,
    StandardDropdown,
    StatusMessage,
    PageLayout
} from '../components/Layout';

function getJwtExpiry(accessToken) {
    if (!accessToken) return null;

    const parts = accessToken.split(".");
    if (parts.length !== 3) return null;

    const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );

    if (!payload.exp) return null;

    return new Date(payload.exp * 1000);
}

/**
 * Workflows hosts the Workflows application for workflow management.
 * The Workflows app is loaded via an iframe for isolation and independent deployment.
 * Authentication is shared via postMessage with an OBO token for the Workflows app's API scope.
 */
class Workflows extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            message: "",
            error: "",
            regionNameChosen: "",
            regionNames: [],
            regionDetailsMap: {},
            workflowAccessToken: "",
            isIframeLoaded: false
        };

        this.iframeRef = React.createRef();
        this.tokenRefreshInterval = null;
    }

    componentDidMount() {
        this.initializeSettings();
    }

    componentDidUpdate(prevProps, prevState) {
        // Send auth to iframe when both iframe is loaded and token is available
        if (this.state.isIframeLoaded && this.state.workflowAccessToken &&
            (!prevState.isIframeLoaded || !prevState.workflowAccessToken)) {
            this.sendAuthToWorkflows();
        }

        // Set up token refresh when iframe is loaded and token is available
        if (prevState.workflowAccessToken !== this.state.workflowAccessToken ||
            prevState.isIframeLoaded !== this.state.isIframeLoaded) {
            if (this.state.isIframeLoaded && this.state.workflowAccessToken) {
                this.startTokenRefresh();
            } else {
                this.stopTokenRefresh();
            }
        }
    }

    componentWillUnmount() {
        this.stopTokenRefresh();
    }

    startTokenRefresh() {
        if (this.tokenRefreshInterval) return;

        // Refresh token every 50 minutes (tokens typically last 60 minutes)
        this.tokenRefreshInterval = setInterval(() => {
            this.acquireAndSendToken();
        }, 50 * 60 * 1000);
    }

    stopTokenRefresh() {
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
            this.tokenRefreshInterval = null;
        }
    }

    async acquireAndSendToken() {
        const { regionNameChosen, regionDetailsMap } = this.state;
        const region = regionDetailsMap[regionNameChosen];

        if (!region?.workflowsScope || !this.props.userAgentApplication) {
            return;
        }

        try {
            const response = await this.props.userAgentApplication.acquireTokenSilent([region.workflowsScope]);
            const token = response.accessToken || response;

            this.setState({
                workflowAccessToken: token
            }, () => {
                // Send the refreshed token to the iframe
                this.sendAuthToWorkflows();
            });
        } catch (err) {
            console.error(err);
        }
    }

    initializeSettings() {
        this.getRegionDetails();
    }

    async getRegionDetails() {
        this.setState({ loading: true, error: "", message: "" });
        try {
            let response = await getRegionDetails(this.props.accessToken);

            if (response.status === 200) {
                let data = await response.json();

                // Filter regions that have WorkflowsUrl and WorkflowsScope configured
                const regionsWithWorkflows = data.filter(region => 
                    region.regionWorkflowsUrl && region.regionWorkflowsScope
                );

                if (regionsWithWorkflows.length === 0) {
                    this.setState({
                        loading: false,
                        error: "No regions with Workflows configuration found.",
                        message: ""
                    });
                    return;
                }

                // Map region details to dropdown format
                const mappedRegions = regionsWithWorkflows.map(region => ({
                    key: region.regionName,
                    text: region.regionName
                }));

                // Create a lookup map for region details including workflow-specific config
                const detailsMap = {};
                regionsWithWorkflows.forEach(region => {
                    detailsMap[region.regionName] = {
                        name: region.regionName,
                        url: region.regionPublicUrl || region.regionUrl,
                        allowSwitch: region.regionAllowSwitch,
                        workflowsUrl: region.regionWorkflowsUrl,
                        workflowsScope: region.regionWorkflowsScope
                    };
                });

                this.setState({
                    loading: false,
                    regionNames: mappedRegions,
                    regionDetailsMap: detailsMap
                });
            } else {
                this.setState({ 
                    loading: false, 
                    error: "Failed to load regions. Please try again.", 
                    message: "" 
                });
            }
        } catch (error) {
            this.setState({ 
                loading: false, 
                error: "Failed to load regions. Please check your connection.", 
                message: "" 
            });
        }
    }

    async acquireWorkflowToken() {
        const { regionNameChosen, regionDetailsMap } = this.state;

        if (!regionNameChosen) {
            return;
        }

        const region = regionDetailsMap[regionNameChosen];
        if (!region?.workflowsScope) {
            this.setState({ error: "Workflows is not configured for this region." });
            return;
        }

        if (!this.props.userAgentApplication) {
            this.setState({ error: "Authentication system not initialized" });
            return;
        }

        const user = this.props.userAgentApplication.getUser?.();
        if (!user) {
            this.setState({ error: "User not authenticated. Please sign in." });
            return;
        }

        this.setState({ loading: true, error: "", message: "" });

        try {
            // MSAL v1 returns a raw access token string here
            const response = await this.props.userAgentApplication.acquireTokenSilent([region.workflowsScope]);
            const token = response.accessToken || response;

            this.setState({
                loading: false,
                workflowAccessToken: token
            });
        } catch (err) {
            try {
                const response = await this.props.userAgentApplication.acquireTokenPopup([region.workflowsScope]);
                const token = response.accessToken || response;

                this.setState({
                    loading: false,
                    workflowAccessToken: token
                });
            } catch (popupError) {
                this.setState({
                    loading: false,
                    error: "Failed to acquire workflow access token. Please try again.",
                    workflowAccessToken: ""
                });
            }
        }
    }

    sendAuthToWorkflows() {
        const { workflowAccessToken, regionNameChosen, regionDetailsMap } = this.state;
        const selectedRegion = regionDetailsMap[regionNameChosen];

        if (!workflowAccessToken || !this.iframeRef.current?.contentWindow || !selectedRegion?.workflowsUrl) {
            return;
        }

        const expiryDate = getJwtExpiry(workflowAccessToken);
        const user = this.props.userAgentApplication.getUser?.();

        const authMessage = {
            type: "AUTH_CONTEXT",
            token: workflowAccessToken,
            expiry: expiryDate ? expiryDate.toISOString() : "",
            userInfo: {
                name: user?.name || "",
                email: user?.displayableId || user?.userName || ""
            }
        };

        const origin = new URL(selectedRegion.workflowsUrl).origin;
        this.iframeRef.current.contentWindow.postMessage(authMessage, origin);
    }

    getWorkflowsAppUrl() {
        const { regionNameChosen, regionDetailsMap } = this.state;
        const selectedRegion = regionDetailsMap[regionNameChosen];

        if (!selectedRegion?.workflowsUrl) return "";

        const url = new URL(selectedRegion.workflowsUrl);
        url.searchParams.set("embedded", "true");
        return url.toString();
    }

    onSelectRegionName = (event, item) => {
        if (!item) {
            return;
        }

        // Stop any existing refresh interval
        this.stopTokenRefresh();

        // Reset state when switching regions
        this.setState({
            regionNameChosen: item.key,
            workflowAccessToken: "",
            isIframeLoaded: false,
            error: "",
            message: ""
        }, () => {
            // Acquire token after state is updated
            this.acquireWorkflowToken();
        });
    };

    handleIframeLoad = () => {
        this.setState({ isIframeLoaded: true });
    };

    render() {
        const {
            loading,
            error,
            message,
            regionNameChosen,
            regionNames,
            workflowAccessToken
        } = this.state;

        const workflowsAppUrl = this.getWorkflowsAppUrl();
        const showInfoMessage = regionNameChosen && !workflowAccessToken && !loading && !error;
        const showIframe = regionNameChosen && workflowAccessToken && workflowsAppUrl;

        return (
            <PageLayout>
                <StandardScreen
                    title="Workflows"
                    description="Manage workflows in the selected environment"
                    loading={loading}
                >
                    <StandardDropdown
                        label="Environment"
                        selectedKey={regionNameChosen}
                        onChange={this.onSelectRegionName}
                        placeholder="Select Environment"
                        options={regionNames}
                    />

                    {error && <StatusMessage type="error">{error}</StatusMessage>}
                    {message && <StatusMessage type="success">{message}</StatusMessage>}

                    {showInfoMessage && (
                        <StatusMessage type="info">
                            Acquiring access token for {regionNameChosen}...
                        </StatusMessage>
                    )}

                    {showIframe && (
                        <div style={{ marginTop: "20px" }}>
                            <iframe
                                key={regionNameChosen}
                                ref={this.iframeRef}
                                src={workflowsAppUrl}
                                width={this.props.width || "100%"}
                                height={this.props.height || "800"}
                                title="Workflows"
                                onLoad={this.handleIframeLoad}
                                style={{ border: "none" }}
                            />
                        </div>
                    )}
                </StandardScreen>
            </PageLayout>
        );
    }
}

export default Workflows;